import type { ThemeMeta, ThemeTokens } from '@/previewThemes/meta';

/**
 * Decor's design constants.
 *
 * Named Decor in the gallery, `hyper` on disk. The id is the folder name and
 * a merchant's stored theme is keyed on it - `preset`, `layout.family` and
 * every `hyper.*` content override - so it stays put; the label is the only
 * part a merchant reads. Tools & Machine does the same thing.
 *
 * A modern furniture superstore: a cobalt utility rail over a white masthead,
 * a rail of circular shelf tiles standing between the navigation and the
 * banner, and cut-out photography on one pale grey throughout. The half of the
 * theme with no React in it.
 */
export const tokens: ThemeTokens = {
  // A geometric humanist sans with a wide, open lowercase and real weight at
  // the top end - which is what lets the wordmark sit at 800 and a section
  // heading at 700 without either reading as a different family. It publishes
  // 200-800, so the default weight request needs no entry in `FAMILY_WEIGHTS`.
  headingFont: 'Plus Jakarta Sans',
  // The sale signal, and the one colour on this shop that is not the brand.
  // Not the primary - the primary is the cobalt a merchant may repaint, while
  // this is the red the Sale tile, the "On Sale" link and the clearance rule
  // in the footer are painted with. It belongs to the drawing rather than to
  // the merchant, the same way Tools & Machine's hazard yellow does.
  accent: '#c6301c',
  // No machine face. Every number on this shop - price, was-price, the count
  // in the basket - is set in the same sans as everything else, at a heavier
  // weight, which is what a furniture shop does and a parts catalogue does not.
  monoFont: null,
  // Barely negative. This face is wide, and a display heading set solid closes
  // up just enough to read as one phrase rather than a row of words.
  headingTracking: '-0.02em',
  headingTransform: 'none',
  // 1440px. A superstore page: six shelf tiles across, five products across,
  // and two offer cards side by side all want the same wide column, and
  // anything narrower takes a tile off the row rather than tightening it.
  containerMax: '90rem',
  // A hair under 1. This is a long home page with a dozen bands on it, and the
  // classic spacing between all of them makes a shop that has to be scrolled
  // past rather than read.
  spaceScale: 0.95,
  // The four decks: the cobalt utility rail, the masthead with the search box
  // in it, the category bar, and the rail of circular shelf tiles under it.
  // The tallest header in the set, which is what the design is - and it is
  // what a full-height banner has to subtract, so it is measured honestly.
  headerHeight: '17.5rem',
};

export const meta: ThemeMeta = {
  label: 'Decor',
  hint: 'A modern furniture superstore: cobalt utility rail, circular shelf tiles and cut-out photography.',
  industries: ['home_kitchen', 'furniture', 'general'],
  look: {
    /**
     * Four colours and their jobs, which is the whole palette:
     *
     * - the cobalt is the *primary*, and so the one a merchant repaints: the
     *   utility rail above the masthead, every button on the shop, the social
     *   band at the foot of the home page and the price of anything reduced;
     * - white is the ground, and the design keeps it that way - this theme
     *   separates with grey tiles and hairlines rather than with bands of
     *   colour, so the photography is the only saturated thing on most of the
     *   page;
     * - the pale grey is the tile: every shelf circle, every product card's
     *   ground and every cut-out's backdrop is this one value, which is what
     *   makes a row of forty unrelated objects read as one range;
     * - the red is the theme's own `accent` in `tokens`, not a merchant
     *   setting - see the note there.
     */
    colors: {
      primary: '#1837bc',
      background: '#ffffff',
      surface: '#f0f0f0',
      text: '#141414',
      muted: '#6e7178',
    },
    typography: {
      font_family: 'Plus Jakarta Sans',
      heading_weight: '700',
    },
    // A pill, not a rounded rectangle. 32px is the most a stored theme may ask
    // for (`ThemeButtons.radius_px`, `le=32`), and on the 48px controls this
    // theme draws, the browser clamps that to a true half-round end - which is
    // the shape every button in the design wears.
    buttons: { radius_px: 32, style: 'solid' },
    // No `catalog` named: this theme ships its own in `catalog.ts`. A furniture
    // superstore is part of its identity - twelve shelves of chairs, tables and
    // lighting, all photographed cut-out on the same grey - and the `home`
    // industry shop is a homewares shop rather than a furniture one.
  },
};
